import { ThemeProvider } from '@emotion/react'
import {
  Box, Button, Chip, CircularProgress, Container, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material'
import MUIDataTable from 'mui-datatables'
import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import api from '../../services/api'
import theme from '../../themeDash'
import KPICard from '../KPICard'
import DateTimeToolbar from '../DateTimeToolbar'
import moment from 'moment'
import { textLabels } from '../textLabelsPT'
import { blue, grey, orange, red } from '@mui/material/colors'

function formatHHmm(seconds) {
  if (!seconds && seconds !== 0) return '-'
  const secs = seconds === 0 ? 120 : Math.abs(seconds)
  const h = Math.floor(secs / 3600)
  const m = Math.round((secs % 3600) / 60)
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

function humanizeDuration(seconds) {
  if (!seconds || seconds <= 0) return '0s'
  const d = moment.duration(seconds, 'seconds')
  const h = Math.floor(d.asHours())
  const m = d.minutes()
  if (h > 0) return `${h}h ${m}m`
  return `${m}m ${d.seconds()}s`
}

export default function ApiDown() {
  const [period, setPeriod] = useState('24h')
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState({ results: [], total: 0, limit: 30, summary: null })
  const [companyMap, setCompanyMap] = useState({})
  const [horusMap, setHorusMap] = useState({})
  const didAutoLoad = useRef(false)

  useLayoutEffect(() => {
    document.title = 'Horus - Relatórios de API Offline'
  })

  // Carrega empresas Athena + Horus para classificar cada evento
  useEffect(() => {
    Promise.all([
      api.get('/empresa-athena/list').catch(() => ({ data: [] })),
      api.get('/empresa/all').catch(() => ({ data: [] })),
    ]).then(([athenaRes, horusRes]) => {
      const aMap = {}
      athenaRes.data.forEach(e => {
        aMap[e.cod_empresa] = e.fantasy_name || e.name || String(e.cod_empresa)
      })
      const hMap = {}
      horusRes.data.forEach(e => {
        hMap[e.cod_empresa] = e.nome_fantasia || e.razao_social || String(e.cod_empresa)
      })
      setCompanyMap(aMap)
      setHorusMap(hMap)
    })
  }, [])

  // Classifica os resultados em 3 grupos:
  // - linked: existe no Athena (com cadastro completo)
  // - orphanAthena: existe no Horus mas nao no Athena (falta cadastro Athena)
  // - orphanTotal: nao existe em lugar nenhum (evento fantasma)
  const { linked, orphanAthena, orphanTotal } = useMemo(() => {
    const linked = []
    const orphanAthena = []
    const orphanTotal = []
    for (const row of data.results) {
      const inAthena = !!companyMap[row.cod_empresa]
      const inHorus = !!horusMap[row.cod_empresa]
      if (inAthena) linked.push(row)
      else if (inHorus) orphanAthena.push(row)
      else orphanTotal.push(row)
    }
    return { linked, orphanAthena, orphanTotal }
  }, [data.results, companyMap, horusMap])

  // Auto-load on mount
  useEffect(() => {
    if (!didAutoLoad.current) {
      didAutoLoad.current = true
      fetchWithPeriod('24h')
    }
  }, [])

  const handlePeriod = (_, val) => {
    if (!val) return
    setPeriod(val)
    fetchWithPeriod(val)
  }

  const fetchWithPeriod = (p, page = 0, limit = 30) => {
    setLoading(true)
    setData(d => ({ ...d, results: [], total: 0 }))

    api.get('report/api-down-summary/', { params: { period: p, page: page + 1, limit } })
      .then(res => {
        setData({
          results: res.data.results || [],
          total: res.data.total || 0,
          limit,
          summary: res.data.summary || null,
        })
      })
      .catch(err => {
        console.error(err)
        setData({ results: [], total: 0, limit: 30, summary: null })
      })
      .finally(() => setLoading(false))
  }

  // Manual search via Buscar
  const handleManualSearch = () => {
    setPeriod(null)
    setLoading(true)
    setData(d => ({ ...d, results: [], total: 0 }))

    const skip = 0
    const limit = data.limit
    api.post('report/api_down', { skip, limit, start: startDate, end: endDate })
      .then(res => {
        setData({
          results: res.data.results || [],
          total: res.data.total || 0,
          limit,
          summary: null,
        })
      })
      .catch(err => {
        console.error(err)
        setData({ results: [], total: 0, limit: 30, summary: null })
      })
      .finally(() => setLoading(false))
  }

  const summary = data.summary || {}

  const columns = [
    {
      name: 'cod_empresa',
      label: 'Empresa',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (cod) => {
          const athenaName = companyMap[cod]
          const horusName = horusMap[cod]
          if (athenaName) {
            return (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{athenaName}</Typography>
                <Typography variant="caption" sx={{ color: grey[500] }}>cod {cod}</Typography>
              </Box>
            )
          }
          if (horusName) {
            return (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Chip label="Sem Athena" size="small" sx={{ bgcolor: orange[800], color: '#fff', fontSize: 10 }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{horusName}</Typography>
                </Box>
                <Typography variant="caption" sx={{ color: grey[500] }}>cod {cod}</Typography>
              </Box>
            )
          }
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Chip label="Fantasma" size="small" sx={{ bgcolor: red[700], color: '#fff', fontSize: 10 }} />
              <Typography variant="body2">cod {cod}</Typography>
            </Box>
          )
        },
      },
    },
    {
      name: 'source',
      label: 'Fonte',
      options: {
        filter: true,
        sort: true,
        customBodyRender: value => (
          <Chip
            label={value === 'wazuh' ? 'Wazuh' : 'Zabbix'}
            size="small"
            sx={{
              bgcolor: value === 'wazuh' ? orange[700] : blue[600],
              color: '#fff',
              fontWeight: 600,
              fontSize: 11,
            }}
          />
        ),
      },
    },
    {
      name: 'created_at',
      label: 'Quando',
      options: {
        filter: false,
        sort: true,
        customBodyRender: value => (
          <span>{moment(value).locale('pt-br').format('DD-MM-YYYY HH:mm:ss')}</span>
        ),
      },
    },
    {
      name: 'updated_at',
      label: 'Finalizado',
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: dataIndex => {
          const row = data.results[dataIndex]
          if (!row) return null
          const val = row.updated_at
          const created = row.created_at
          const display = val === created
            ? moment(val).add(2, 'minutes').format('DD-MM-YYYY HH:mm:ss')
            : moment(val).locale('pt-br').format('DD-MM-YYYY HH:mm:ss')
          return <span>{display}</span>
        },
      },
    },
    {
      name: 'time_seconds_api_down',
      label: 'Duração',
      options: {
        filter: false,
        sort: true,
        customBodyRender: value => <span>{formatHHmm(value)}</span>,
      },
    },
  ]

  const handleTableChange = (action, tableState) => {
    const { page, rowsPerPage } = tableState
    switch (action) {
      case 'changePage':
      case 'changeRowsPerPage':
        if (period) {
          fetchWithPeriod(period, action === 'changePage' ? page : 0, rowsPerPage)
        }
        break
      default:
        break
    }
  }

  const options = {
    search: false,
    textLabels,
    filterType: 'checkbox',
    rowsPerPageOptions: [10, 30, 50, 100, 500],
    rowsPerPage: data.limit,
    count: data.total,
    serverSide: true,
    rowHover: false,
    print: true,
    selectableRows: 'none',
    download: true,
    onTableChange: handleTableChange,
  }

  return (
    <Container sx={{ mt: 4, mb: 4 }} maxWidth={false}>
      <ThemeProvider theme={theme}>
        {/* Toolbar */}
        <Box display="flex" alignItems="center" flexWrap="wrap" sx={{ bgcolor: 'background.paper', p: 1, boxShadow: 1, mb: 2 }}>
          <ToggleButtonGroup value={period} exclusive onChange={handlePeriod} size="small" sx={{ m: 1 }}>
            <ToggleButton value="24h" sx={{ color: grey[300] }}>24h</ToggleButton>
            <ToggleButton value="7d" sx={{ color: grey[300] }}>7d</ToggleButton>
            <ToggleButton value="30d" sx={{ color: grey[300] }}>30d</ToggleButton>
          </ToggleButtonGroup>
          <DateTimeToolbar label={'Data Inicial'} handler={(v) => { setStartDate(v); setPeriod(null) }} initValue={moment().subtract(1, 'days')} />
          <DateTimeToolbar label={'Data Final'} handler={(v) => { setEndDate(v); setPeriod(null) }} />
          <Button onClick={handleManualSearch} disabled={!(startDate) || !(endDate) || loading}
            variant="contained" color="primary" sx={{ m: 1 }}>Buscar</Button>
        </Box>

        {/* KPI Cards */}
        {summary.total_events != null && (
          <Box display="flex" flexWrap="wrap" justifyContent="center" mb={2}>
            <KPICard title="Total Eventos" value={summary.total_events} />
            <KPICard
              title="Tempo Total Offline"
              value={summary.total_downtime_seconds != null ? humanizeDuration(summary.total_downtime_seconds) : '--'}
            />
            <KPICard title="Empresa Mais Afetada" value={summary.most_affected ?? '--'} />
            {orphanAthena.length > 0 && (
              <KPICard title="Sem Athena (página)" value={orphanAthena.length} />
            )}
            {orphanTotal.length > 0 && (
              <KPICard title="Fantasma (página)" value={orphanTotal.length} />
            )}
          </Box>
        )}

        {/* Tabela principal: vinculadas com Athena */}
        <Box>
          <MUIDataTable
            title={`Vinculadas com Athena${linked.length ? ` (${linked.length}/${data.total})` : ''}`}
            data={linked}
            columns={columns}
            options={{
              ...options,
              count: data.total,
              textLabels: {
                ...textLabels,
                body: {
                  noMatch: loading
                    ? <CircularProgress color="info" />
                    : 'Nenhum registro vinculado nesta página',
                },
              },
            }}
          />
        </Box>

        {/* Orfas Athena: existem no Horus mas nao no Athena */}
        {orphanAthena.length > 0 && (
          <Box sx={{ mt: 3, borderLeft: `4px solid ${orange[800]}`, pl: 1.5 }}>
            <MUIDataTable
              title={`Sem cadastro no Athena (${orphanAthena.length} nesta página) — empresas no Horus que precisam ser cadastradas no Athena`}
              data={orphanAthena}
              columns={columns}
              options={{
                search: false,
                textLabels,
                filterType: 'checkbox',
                rowsPerPageOptions: [10, 30, 50],
                rowsPerPage: 30,
                rowHover: false,
                print: false,
                selectableRows: 'none',
                download: true,
                pagination: orphanAthena.length > 30,
              }}
            />
          </Box>
        )}

        {/* Fantasmas: cod_empresa nao existe nem no Horus nem no Athena */}
        {orphanTotal.length > 0 && (
          <Box sx={{ mt: 3, borderLeft: `4px solid ${red[700]}`, pl: 1.5 }}>
            <MUIDataTable
              title={`Eventos fantasma (${orphanTotal.length} nesta página) — cod_empresa não existe no Horus nem no Athena`}
              data={orphanTotal}
              columns={columns}
              options={{
                search: false,
                textLabels,
                filterType: 'checkbox',
                rowsPerPageOptions: [10, 30, 50],
                rowsPerPage: 30,
                rowHover: false,
                print: false,
                selectableRows: 'none',
                download: true,
                pagination: orphanTotal.length > 30,
              }}
            />
          </Box>
        )}
      </ThemeProvider>
    </Container>
  )
}
